"use client"

import { LayoutDashboard, Package, ShoppingCart, Users, Settings, Menu, X, LogOut, ImageIcon, AlertCircle } from "lucide-react"
import { useState, useEffect } from "react"
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom"
import NotificationBell from "../../components/admin/NotificationBell"
import { useNotifications } from "../../contexts/NotificationContext"
const Logo = "/Logo.png";

const AdminLayout = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const location = useLocation()
  const navigate = useNavigate()
  useNotifications()

  // Lock browser scroll to the AdminLayout while allowing main to scroll
  useEffect(() => {
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = prevOverflow
    }
  }, [])

  // Close menu when route changes on mobile
  useEffect(() => {
    setIsMenuOpen(false)
  }, [location])

  // Close menu when clicking outside on mobile
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const sidebar = document.getElementById("sidebar")
      const menuButton = document.getElementById("menu-button")
      if (
        isMenuOpen &&
        sidebar &&
        !sidebar.contains(event.target as Node) &&
        menuButton &&
        !menuButton.contains(event.target as Node)
      ) {
        setIsMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [isMenuOpen])

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      {/* Mobile Overlay */}
      {isMenuOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden" onClick={() => setIsMenuOpen(false)} />
      )}

      {/* Sidebar */}
      <div
        id="sidebar"
        className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-gray-800 text-white transform transition-transform duration-200 ease-in-out ${
          isMenuOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        }`}
      >
        <div className="flex items-center justify-between p-4 border-b border-gray-700">
          <Link to="/" className="flex items-center space-x-2 border border-black bg-white px-2 py-1 rounded">
            <img src={Logo || "/placeholder.svg"} alt="Anmol Furniture Bazar Logo" className="h-10 w-auto" />
          </Link>

          <button
            id="menu-button"
            className="lg:hidden p-2 rounded-md hover:bg-gray-700 focus:outline-none"
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="mt-6 px-4 space-y-2">
          <Link
            to="/admin"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname === "/admin" ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <LayoutDashboard className="w-5 h-5" />
            <span>Dashboard</span>
          </Link>
          <Link
            to="/admin/products"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/products") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <Package className="w-5 h-5" />
            <span>Products</span>
          </Link>
          <Link
            to="/admin/orders"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/orders") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <ShoppingCart className="w-5 h-5" />
            <span>Orders</span>
          </Link>
          <Link
            to="/admin/customers"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/customers") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <Users className="w-5 h-5" />
            <span>Customers</span>
          </Link>
          <Link
            to="/admin/banners"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/banners") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <ImageIcon className="w-5 h-5" />
            <span>Banner Management</span>
          </Link>
          <Link
            to="/admin/enquiries"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/enquiries") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <AlertCircle className="w-5 h-5" />
            <span>Enquiries</span>
          </Link>
          <Link
            to="/admin/settings"
            className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${
              location.pathname.startsWith("/admin/settings") ? "bg-gray-700" : "hover:bg-gray-700"
            }`}
          >
            <Settings className="w-5 h-5" />
            <span>Settings</span>
          </Link>
          <Link
            to="/admin/logout"
            className="flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors hover:bg-gray-700"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </Link>
        </nav>
      </div>

      {/* Main Column */}
      <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
        {/* Header */}
        <header className="bg-white shadow-sm flex-shrink-0">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-gray-100 focus:outline-none"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
            >
              <Menu className="h-6 w-6 text-gray-600" />
            </button>
            <div className="flex items-center ml-auto gap-2">
              <NotificationBell />
              <button
                type="button"
                onClick={() => navigate("/admin/logout")}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-red-600 transition-colors"
                aria-label="Logout"
              >
                <LogOut className="w-6 h-6" />
              </button>
            </div>
          </div>
        </header>

        {/* Main Content: only this scrolls */}
        <main className="flex-1 min-h-0 overflow-x-hidden overflow-y-auto bg-gray-100">
          <div className="container mx-auto px-4 py-6">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  )
}

export default AdminLayout
